import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProjectsSection = ({ projects, onAdd, onDelete }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [expandedProject, setExpandedProject] = useState(null);
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    technologies: '',
    startDate: '',
    endDate: '',
    projectUrl: '',
    githubUrl: ''
  });

  const handleAddProject = () => {
    if (newProject?.title && newProject?.description) {
      onAdd(newProject);
      setNewProject({
        title: '',
        description: '',
        technologies: '',
        startDate: '',
        endDate: '',
        projectUrl: '',
        githubUrl: ''
      });
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-card-foreground">
          Projects Portfolio
        </h3>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setIsAdding(!isAdding)}
        >
          Add Project
        </Button>
      </div>
      {isAdding && (
        <div className="bg-muted/50 rounded-lg p-4 md:p-6 border border-border space-y-4">
          <Input
            label="Project Title"
            type="text"
            placeholder="E-commerce Website, Mobile App, etc."
            value={newProject?.title}
            onChange={(e) => setNewProject({ ...newProject, title: e?.target?.value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Project Description <span className="text-error">*</span>
            </label>
            <textarea
              className="w-full px-4 py-3 rounded-md border border-input bg-background text-card-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              rows="4"
              placeholder="Describe your project, its features, and your role..."
              value={newProject?.description}
              onChange={(e) => setNewProject({ ...newProject, description: e?.target?.value })}
            />
          </div>

          <Input
            label="Technologies Used"
            type="text"
            placeholder="React, Node.js, MongoDB, etc. (comma separated)"
            value={newProject?.technologies}
            onChange={(e) => setNewProject({ ...newProject, technologies: e?.target?.value })}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              value={newProject?.startDate}
              onChange={(e) => setNewProject({ ...newProject, startDate: e?.target?.value })}
            />
            <Input
              label="End Date"
              type="date"
              value={newProject?.endDate}
              onChange={(e) => setNewProject({ ...newProject, endDate: e?.target?.value })}
            />
          </div>

          <Input
            label="Project URL"
            type="url"
            placeholder="https://yourproject.com"
            value={newProject?.projectUrl}
            onChange={(e) => setNewProject({ ...newProject, projectUrl: e?.target?.value })}
          />

          <Input
            label="GitHub Repository"
            type="url"
            placeholder="https://github.com/username/repo"
            value={newProject?.githubUrl}
            onChange={(e) => setNewProject({ ...newProject, githubUrl: e?.target?.value })}
          />

          <div className="flex gap-3">
            <Button variant="default" onClick={handleAddProject}>
              Save Project
            </Button>
            <Button variant="outline" onClick={() => setIsAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-4">
        {projects?.map((project, index) => (
          <div
            key={index}
            className="bg-card rounded-lg border border-border overflow-hidden transition-smooth hover:shadow-warm-md"
          >
            <div
              className="p-4 md:p-6 cursor-pointer"
              onClick={() => setExpandedProject(expandedProject === index ? null : index)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="FolderGit2" size={20} color="var(--color-accent)" />
                    </div>
                    <div>
                      <h4 className="text-base md:text-lg font-semibold text-card-foreground">
                        {project?.title}
                      </h4>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {project?.description}
                      </p>
                    </div>
                  </div>
                  {project?.technologies && (
                    <div className="flex flex-wrap gap-2 ml-13">
                      {project?.technologies?.split(',')?.map((tech, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-md"
                        >
                          {tech?.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <Icon
                  name={expandedProject === index ? 'ChevronUp' : 'ChevronDown'}
                  size={20}
                  color="var(--color-muted-foreground)"
                />
              </div>
            </div>

            {expandedProject === index && (
              <div className="px-4 md:px-6 pb-4 md:pb-6 border-t border-border">
                <div className="pt-4 space-y-3">
                  <p className="text-sm text-card-foreground">{project?.description}</p>
                  {(project?.startDate || project?.endDate) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Calendar" size={14} />
                      <span>
                        {project?.startDate && new Date(project.startDate)?.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                        {project?.startDate && project?.endDate && ' - '}
                        {project?.endDate && new Date(project.endDate)?.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 pt-2">
                    {project?.projectUrl && (
                      <a
                        href={project?.projectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <Icon name="ExternalLink" size={14} />
                        View Project
                      </a>
                    )}
                    {project?.githubUrl && (
                      <a
                        href={project?.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <Icon name="Github" size={14} />
                        View Code
                      </a>
                    )}
                    <button
                      onClick={() => onDelete(index)}
                      className="text-sm text-error hover:underline flex items-center gap-1"
                    >
                      <Icon name="Trash2" size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {projects?.length === 0 && !isAdding && (
        <div className="text-center py-12 bg-muted/30 rounded-lg border border-dashed border-border">
          <Icon name="FolderGit2" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No projects added yet</p>
          <Button variant="outline" onClick={() => setIsAdding(true)}>
            Add Your First Project
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProjectsSection;